export type CodePaneFileKind = 'notebook' | 'text' | 'preview';
export type PreviewFileKind = 'image' | 'pdf' | 'html' | 'video' | 'audio' | 'unsupported';

const TEXT_EXTENSIONS = new Set([
  '.py',
  '.pyi',
  '.js',
  '.jsx',
  '.ts',
  '.tsx',
  '.mjs',
  '.cjs',
  '.json',
  '.jsonl',
  '.yaml',
  '.yml',
  '.toml',
  '.ini',
  '.cfg',
  '.conf',
  '.md',
  '.mdx',
  '.txt',
  '.rst',
  '.csv',
  '.tsv',
  '.xml',
  '.urdf',
  '.usd',
  '.usda',
  '.log',
]);

const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp']);
const PDF_EXTENSIONS = new Set(['.pdf']);
const HTML_EXTENSIONS = new Set(['.html', '.htm']);
const VIDEO_EXTENSIONS = new Set(['.mp4', '.webm', '.mov', '.m4v']);
const AUDIO_EXTENSIONS = new Set(['.mp3', '.wav', '.ogg', '.m4a']);

export function fileExtension(path: string): string {
  const name = fileName(path).toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot) : '';
}

export function fileName(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? path;
}

export function codePaneFileKind(path: string): CodePaneFileKind | null {
  const ext = fileExtension(path);
  if (ext === '.ipynb') return 'notebook';
  if (TEXT_EXTENSIONS.has(ext)) return 'text';
  if (previewFileKind(path) !== 'unsupported') return 'preview';
  return 'preview';
}

export function previewFileKind(path: string): PreviewFileKind {
  const ext = fileExtension(path);
  if (IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (PDF_EXTENSIONS.has(ext)) return 'pdf';
  if (HTML_EXTENSIONS.has(ext)) return 'html';
  if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
  return 'unsupported';
}

export function mimeTypeForPath(path: string): string {
  const ext = fileExtension(path);
  switch (ext) {
    case '.png':
      return 'image/png';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    case '.svg':
      return 'image/svg+xml';
    case '.bmp':
      return 'image/bmp';
    case '.pdf':
      return 'application/pdf';
    case '.html':
    case '.htm':
      return 'text/html';
    case '.mp4':
    case '.m4v':
      return 'video/mp4';
    case '.webm':
      return 'video/webm';
    case '.mov':
      return 'video/quicktime';
    case '.mp3':
      return 'audio/mpeg';
    case '.wav':
      return 'audio/wav';
    case '.ogg':
      return 'audio/ogg';
    case '.m4a':
      return 'audio/mp4';
    default:
      return 'application/octet-stream';
  }
}
